<template>
	<article v-if="user">
		<header class="col-md-12 member-header">
			<div class="py-5 row" :class="{'d-flex': mq.current >= mq.md}">
				<div class="col-md-7">
					<router-link :to="routeAvatar" tag="div" class="pointer">
						<ximg
							class="pixelated r-2 pull-left member-avatar"
							:src="'/api/avatar/' + user.id + '/?no-cache=1'"
							:ariaLabel="user.username"
							:style="avatarStyle"
							role="img"
						/>
					</router-link>
					<div class="member-title pl-4 relative">
						<h1 class="member-name">{{ user.username }} <knighthood-icon v-if="user.knighthood > 0" /></h1>
						<router-link :to="routeBenefits" tag="span" class="member-class" v-if="user.avatar.class"><img :src="statIconSrc" alt="member-icon">{{ user.avatar.class.title }}</router-link>
						<div class=" ml-4 ap-b ap-fill-x">
							<perks v-if="isCurrentUser && mediumLayout" :userPerks="user.userPerks" class="mb-2" />
							<div class="achievements" :style="achievementsStyle">
								<div class="achievement-icons">
									<router-link
										v-for="achievement in computedAchievements"
										style="position: relative;"
										:style="achievementsStyle"
										:key="achievement.id"
										:title="achievement.name"
										:to="{
											name: 'member.achievements',
											params: Object.assign({}, $route.params, {
												aid: achievement.id,
											}),
										}"
									>
										<ximg
											class="achievement-icon"
											:src="convertToAchievementUrl(achievement.imageName, 47, 47)"
											:fallback="convertToAchievementUrl('404', 47, 47)"
											:ariaLabel="achievement.name + ' achievement icon'"
											role="img"
										/>
										<span v-if="achievement.modifiedAt > lastNewAchievementTimestamp" class="ap-tr m-1 new-bubble">
											{{ t('NEW') }}
										</span>
									</router-link>
								</div>
								<router-link
									v-if="mediumLayout"
									tag="div"
									class="cheevo-counter-link"
									:to="{
										name: 'member.achievements',
										params: { id: user.id }
									}"
								>
									<cheevo-counter
										:achievementCount="user.unlockedAchievementCount"
										:inProgressCount="user.inProgressAchievementCount"
									/>
								</router-link>
							</div>
						</div>
					</div>
				</div>
				<div class="member-stats col-md-5">
					<router-link class="member-progress" :to="routeBenefits" tag="div">
						<xp-bar :user="user" class="mb-3" />
						<achievement-bar :user="user" class="mb-3" v-if="staticData.achievementsToNextRankLevel[user.rankLevel] > 0" />
						<div class="text-nowrap pull-left lvl-text">
							Level {{ user.rankLevel }} <span class="rank-name" :class="rankGroup.class" :data-text="rankGroup.dataText">{{ rankGroup.name }}</span>
						</div>
						<div
							v-if="isCurrentUser"
							class="member-stats-current-user"
						>
							<span class="text-highlight">{{ formatNumber(user.experiencePoints) }}</span><span v-if="staticData.xpToNextRankLevel[user.rankLevel]"> / {{ formatNumber(staticData.xpToNextRankLevel[user.rankLevel]) }}</span> XP<br>
							<span
								v-if="staticData.achievementsToNextRankLevel[user.rankLevel] > 0"
								class="member-stats-achievements"
							>
								<span class="text-primary">{{ formatNumber(user.unlockedAchievementCount) }}</span> / {{ formatNumber(staticData.achievementsToNextRankLevel[user.rankLevel]) }} unlocked
								<ximg
									src="/img/icons/feed/feed_achievement.svg"
									ariaLabel="achievement"
									role="img"
								/>
							</span>
						</div>
						<div class="text-nowrap text-right pull-right friend-buttons" v-else-if="canAddFriend">
							<span v-if="pendingFriendRequest">
								<span class="btn btn-success btn-sm mr-2" @click="acceptFriendRequest()">Acceptera</span>
								<span class="btn btn-danger btn-sm" @click="denyFriendRequest()">Neka</span>
							</span>
							<span class="btn btn-default btn-sm" v-else @click="addFriend()" :class="{ disabled: friendRequests.length }">
								Lägg till som vän
							</span>
						</div>
						<div  class="text-nowrap text-right pull-right" v-else-if="isFriend">
							<span>
								Du och {{ user.username }} är vänner :D
							</span>
						</div>
					</router-link>
					<div class="clearfix">
						<div class="bottom-stats" :style="bottomStatsStyle" v-if="! mediumLayout">
							<perks v-if="isCurrentUser" class="perks" :userPerks="user.userPerks" />
							<router-link
								tag="div"
								class="cheevo-counter-link"
								:to="{
									name: 'member.achievements',
									params: { id: user.id }
								}"
							>
								<cheevo-counter
									:achievementCount="user.unlockedAchievementCount"
									:inProgressCount="user.inProgressAchievementCount"
								/>
							</router-link>
						</div>
					</div>
				</div>
			</div>

			<user-log v-if="isCurrentUser" :user="currentUser" />
		</header>
		<div class="clearfix" />
		<div v-if="storeCredit && storeCredit.credit > 0" class="p-2 text-center site-message-info">
			<img src="/img/icons/delbetalning_coin.svg" alt="icon store credit" height="13">
			Tillgodo på din profil: <b>{{ formatPrice(storeCredit.credit, storeCredit.currency) }}</b>
		</div>

		<div class="row m-0">
			<member-nav :user="user" :currentUser="currentUser" v-if="isCurrentUser" />
			<router-view :class="['member-subpage', isCurrentUser ? 'col-md-9' : 'col-md-12']" :user="user" />
		</div>
	</article>
</template>

<script>
import waja from 'waja';
import { formatNumber, formatPrice, urlFormat, convertToCdnUrl, convertToAchievementUrl } from 'utils';
import alert from 'alert';
import mq from 'mediaQuery';
import router from 'router';
import store from 'datastore';
import { mapState, mapGetters } from 'vuex';
import { t } from 'datastore/i18n';
import * as staticData from 'staticData';
import { friendStatus } from 'staticData';
import metaHandler from 'metaHandler';
import memberUtils from 'utils/member';

export default {
	store,
	data () {
		return {
			mq,
			user: null,
			staticData,
			friendRequests: [],
			storeCredit: null,
			convertToCdnUrl,
			convertToAchievementUrl,
			urlFormat,

		};
	},
	beforeRouteEnter (to, frm, next) {
		waja.get('user/' + to.params.id)
			.on('success', (res) => {
				if(to.name === 'member.wishlists' && res.data.status === 'Unauthorized') {
					next('/');
				} else {
					next((vm) => {
						vm.user = res.data.user;
						vm.friendRequests = res.data.friendRequests;
					});
				}
			})
			.on('error', () => {
				next('/errors/404');
			})
			.go();
	},
	beforeRouteUpdate (to, frm, next) {
		metaHandler.set('robots', 'noindex, follow');
		next();
	},
	layoutOptions: {
		leftCol: false,
	},
	methods: {
		formatNumber: formatNumber,
		formatPrice: formatPrice,
		tm: t.prefix('MEMBER'),
		t,
		addFriend () {
			// we don't want to run this if there already exists a pending request.
			waja.post('friend-request')
				.data({ userId: this.user.id })
				.on('success', res => {
					if (res.data.status === 'accepted') {
						this.friendRequests = res.data.friendRequests;
						alert('Du är nu vän med ' + this.user.username);
					} else {
						alert('Din vänförfrågan har skickats till ' + this.user.username);
					}
				})
				.on('error', res => {
					switch (res.status) {
						case 403:
							alert('Du har redan skickat en vänförfrågan till ' + this.user.username);
							break;
						default:
							alert('Något gick fel med din vänförfrågan, vänligen försök igen.');
					}
				})
				.go();
		},
		acceptFriendRequest () {
			waja.post('friend-request/'+this.pendingFriendRequest.id+'/accept')
				.on('success', res => {
					alert('Du är nu vän med ' + res.data.friend.username);
					this.friendRequests = res.data.friendRequests;
				}).go();
		},
		denyFriendRequest () {
			waja.post('friend-request/'+this.pendingFriendRequest.id+'/deny')
				.on('success', res => {
					alert('Vänförfrågan nekad.');
					this.friendRequests = [];
				}).go();
		},
	},
	computed: {
		computedAchievements () {
			if (this.mediumLayout) {
				return this.user.achievements.slice(0, 4);
			}
			return this.user.achievements.slice(0, 5);
		},
		mediumLayout () {
			return  mq.current < mq.md;
		},
		smallLayout () {
			return mq.current < mq.sm;
		},
		userHasPerks () {
			return this.user.userPerks.length ? true : false;
		},
		lastNewAchievementTimestamp () {
			return (Date.now() / 1000) - 60 * 60 * 24 * 30;
		},
		isFriend () {
			return this.friendRequests && this.friendRequests.some(fr => fr.status === friendStatus.ACCEPTED);
		},
		pendingFriendRequest () {
			return this.friendRequests && this.friendRequests.find(fr => fr.status === friendStatus.PENDING && fr.sender.id === this.user.id);
		},
		canAddFriend () {
			return ! this.isCurrentUser && this.isLoggedIn && ! this.isFriend;
		},
		statIconSrc () {
			if (! (this.user && this.user.avatar && this.user.avatar.class)) {
				return;
			}
			const statNames = [// @TODO delad datakälla... eller skicka med bildurl från servern
				'Strength',
				'Dexterity',
				'Charm',
				'Holy',
				'Wisdom',
			];
			return convertToCdnUrl('/img/icons/stats/' + statNames[this.user.avatar.class.id - 1] + '.svg');
		},
		xpBarProgress () {
			const xp = staticData.xpToNextRankLevel[this.user.rankLevel - 1];
			const xpNext = staticData.xpToNextRankLevel[this.user.rankLevel] - xp;
			const progress = (this.user.experiencePoints - xp) / xpNext;
			return progress;
		},
		rankGroup () {
			return staticData.rankGroups[staticData.rankGroupMap[this.user.rankLevel]];
		},
		nextRankGroup () {
			return staticData.rankGroups[staticData.rankGroupMap[this.user.rankLevel + 1]];
		},
		avatarStyle () {
			return {
				backgroundImage: memberUtils.getBackgroundUrl(this.user),
			};
		},
		achievementsStyle () {
			let height = 46;

			if (this.smallLayout) {
				height = 28;
			} else if (this.mediumLayout) {
				if (this.isCurrentUser && this.user && this.user.userPerks.length) {
					height = 28;
				}
			}

			height = height + 'px';
			return {
				height,
			};
		},
		bottomStatsStyle () {
			const flexDirection = this.isCurrentUser ? 'row' : 'row-reverse';
			return { flexDirection };
		},
		isCurrentUser () {
			if (! this.user || ! this.currentUser) {
				return false;
			}
			return this.user.id === this.currentUser.id;
		},
		routeAvatar () {
			if (this.isCurrentUser) {
				return this.$router.route({ name: 'member.avatar', params: { id: this.user.id } });
			}
			return '';
		},
		routeBenefits () {
			if (this.isCurrentUser) {
				return this.$router.route({ name: 'member.benefits', params: { id: this.user.id } });
			}
			return '';
		},
		...mapState({
			currentUser: state => state.user,
		}),
		...mapGetters ({
			isLoggedIn: 'user/isLoggedIn',
		}),
	},
	mounted () {
		metaHandler.set('robots', 'noindex, follow');
	},
	watch: {
		$route (to, frm) {
			if (to.params.id === frm.params.id) {
				return;
			}
			waja.get('user/' + to.params.id)
				.on('success', (res) => {
					this.user = res.data.user;
					this.friendRequests = res.data.friendRequests;
				})
				.on('error', () => {
					// @TODO 404
				})
				.go();
		},
		'currentUser.id': function (newId, oldId) {
			if (this.user.id === oldId) {
				// Gå till startsidan om man loggar ut från sin egen profil
				router.push('/');
			}
		},
		user (user) {
			// @TODO fix breadcrumbs
			router.updateParams({
				name: urlFormat(user.username),
			});
		},
		isCurrentUser (isCurrentUser) {
			this.storeCredit = null;

			/*if (isCurrentUser) {
				waja.get('store-credit')
					.on('success', res => {
						this.storeCredit = res.data;
					}).go();
			}*/
		},
	},
};
</script>
